import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../context/AuthContext'
import { useTranslation } from 'react-i18next'
import { PAYMENT_METHOD_CODES, paymentMethodLabel } from '../utils/paymentMethods'
import { uploadReceipt } from '../utils/receipts'
import { deriveInstallmentStatus } from '../utils/agreements'

const INPUT =
  'w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:border-oasis-500 focus:outline-none focus:ring-2 focus:ring-oasis-200 transition bg-white'
const LABEL = 'block text-sm font-medium text-gray-700 mb-1'
const OTHER_CHURCH = '__other__'

function formatDate(dateStr, lang = 'en') {
  if (!dateStr) return '—'
  const locale = lang === 'es' ? 'es-ES' : 'en-US'
  return new Intl.DateTimeFormat(locale, {
    weekday: 'short', month: 'long', day: 'numeric', year: 'numeric',
  })
    .formatToParts(new Date(dateStr + 'T00:00:00'))
    .map(({ type, value }) =>
      lang === 'es' && (type === 'weekday' || type === 'month')
        ? value.charAt(0).toUpperCase() + value.slice(1)
        : value
    )
    .join('')
}

function formatCurrency(amount) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount ?? 0)
}

function formatDateShort(dateStr, lang = 'en') {
  if (!dateStr) return '—'
  const locale = lang === 'es' ? 'es-ES' : 'en-US'
  return new Intl.DateTimeFormat(locale, {
    month: 'short', day: 'numeric', year: 'numeric',
  })
    .formatToParts(new Date(dateStr))
    .map(({ type, value }) =>
      lang === 'es' && type === 'month'
        ? value.charAt(0).toUpperCase() + value.slice(1)
        : value
    )
    .join('')
}

const STATUS_STYLES = {
  Paid:             'bg-green-100 text-green-800 border-green-200',
  'Partial Payment':'bg-blue-100 text-blue-800 border-blue-200',
  Confirmed:        'bg-lime-100 text-lime-800 border-lime-200',
  Pending:          'bg-amber-100 text-amber-800 border-amber-200',
  Cancelled:        'bg-red-100 text-red-800 border-red-200',
  Sponsored:        'bg-purple-100 text-purple-800 border-purple-200',
}

const RECEIPT_STATUS_STYLES = {
  'Pending Review': 'bg-amber-100 text-amber-800 border-amber-200',
  Approved:         'bg-green-100 text-green-800 border-green-200',
  Rejected:         'bg-red-100 text-red-800 border-red-200',
}

function StatusBadge({ status, styles = STATUS_STYLES }) {
  return (
    <span className={`inline-flex items-center rounded-full border px-3 py-0.5 text-xs font-semibold ${styles[status] ?? 'bg-gray-100 text-gray-600 border-gray-200'}`}>
      {status}
    </span>
  )
}

function DetailRow({ label, value }) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">{label}</p>
      <p className="mt-0.5 text-sm font-medium text-gray-800">{value || '—'}</p>
    </div>
  )
}

export default function MyRegistration() {
  const { user } = useAuth()
  const { t, i18n } = useTranslation()

  const [regs, setRegs] = useState([])
  const [receipts, setReceipts] = useState([])   // all payment_receipts for this user
  const [churches, setChurches] = useState([])
  const [pageLoading, setPageLoading] = useState(true)
  const [pageError, setPageError] = useState('')

  // Edit state
  const [editingRegId, setEditingRegId] = useState(null)
  const [editForm, setEditForm] = useState(null)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')

  // Unregister state
  const [confirmingCancelRegId, setConfirmingCancelRegId] = useState(null)
  const [cancelling, setCancelling] = useState(false)

  // Payment upload state — per registration
  const [uploadingRegId, setUploadingRegId] = useState(null)
  const [uploadForm, setUploadForm] = useState({ file: null, amount: '', payment_method: '' })
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState('')
  const [uploadSuccess, setUploadSuccess] = useState(false)

  // Which registrations have their payment history expanded
  const [expandedPayments, setExpandedPayments] = useState(new Set())

  // Payment agreement data — keyed by registration id
  const [agreements, setAgreements] = useState({})
  const [installments, setInstallments] = useState({})
  const [allocations, setAllocations] = useState({})

  useEffect(() => { fetchAll() }, [user.id])

  useEffect(() => {
    supabase
      .from('churches')
      .select('id, name')
      .eq('is_active', true)
      .order('sort_order', { ascending: true })
      .order('name', { ascending: true })
      .then(({ data }) => setChurches(data ?? []))
  }, [])

  async function fetchAll() {
    // Fix #3: handle fetch errors instead of silently showing empty state
    const [
      { data: regsData,     error: regsErr },
      { data: receiptsData, error: receiptsErr },
    ] = await Promise.all([
      supabase
        .from('registrations')
        .select('*, camps(*), pricing_tiers(*)')
        .eq('user_id', user.id)
        .neq('payment_status', 'Cancelled')
        .order('created_at', { ascending: false }),
      supabase
        .from('payment_receipts')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false }),
    ])
    if (regsErr || receiptsErr) {
      setPageError(regsErr?.message ?? receiptsErr?.message ?? 'Failed to load your registrations.')
    } else {
      setRegs(regsData ?? [])
      setReceipts(receiptsData ?? [])

      // Load payment agreements for each registration
      const agreementsMap = {}
      const installmentsMap = {}
      const allocationsMap = {}
      for (const reg of regsData ?? []) {
        const { data: agreementData } = await supabase
          .from('payment_agreements')
          .select('*').eq('registration_id', reg.id).eq('status', 'Active').maybeSingle()

        let installmentData = []
        let allocationData = []
        if (agreementData) {
          const [{ data: insts }, { data: allocs }] = await Promise.all([
            supabase.from('payment_installments').select('*').eq('agreement_id', agreementData.id).order('seq'),
            supabase.from('installment_allocations').select('*').eq('camp_id', agreementData.camp_id),
          ])
          installmentData = insts ?? []
          allocationData = allocs ?? []
        }
        agreementsMap[reg.id] = agreementData ?? null
        installmentsMap[reg.id] = installmentData
        allocationsMap[reg.id] = allocationData
      }
      setAgreements(agreementsMap)
      setInstallments(installmentsMap)
      setAllocations(allocationsMap)
    }
    setPageLoading(false)
  }

  /* ── Helpers ─────────────────────────────────────────────────────────── */

  function receiptsFor(regId) {
    return receipts.filter(r => r.registration_id === regId)
  }

  function togglePaymentExpand(regId) {
    setExpandedPayments(prev => {
      const next = new Set(prev)
      if (next.has(regId)) next.delete(regId)
      else next.add(regId)
      return next
    })
  }

  /* ── Edit helpers ──────────────────────────────────────────────────── */
  function startEditing(reg) {
    setSaveError('')
    // If the stored church isn't one of the active listed options, treat it
    // as a free-typed "Other" value so it survives an edit.
    const isListed = reg.church && churches.some(c => c.name === reg.church)
    setEditForm({
      full_name:               reg.full_name,
      date_of_birth:           reg.date_of_birth,
      sex:                     reg.sex,
      phone:                   reg.phone,
      church:                  reg.church ? (isListed ? reg.church : OTHER_CHURCH) : '',
      church_other:            reg.church && !isListed ? reg.church : '',
      emergency_contact_name:  reg.emergency_contact_name,
      emergency_contact_phone: reg.emergency_contact_phone,
      needs_transport:         reg.needs_transport,
    })
    setEditingRegId(reg.id)
  }

  function stopEditing() {
    setEditingRegId(null)
    setEditForm(null)
    setSaveError('')
  }

  function setField(field, value) {
    setEditForm(prev => ({ ...prev, [field]: value }))
  }

  async function handleSave() {
    setSaveError('')
    setSaving(true)

    const reg = regs.find(r => r.id === editingRegId)
    const baseFee = reg?.pricing_tiers?.price ?? 0
    const transportCost = parseFloat(reg?.camps?.transport_cost ?? 0)
    const newAmountOwed = baseFee + (editForm.needs_transport ? transportCost : 0)

    const church = editForm.church === OTHER_CHURCH ? editForm.church_other.trim() : editForm.church

    const { error } = await supabase
      .from('registrations')
      .update({
        full_name:               editForm.full_name,
        date_of_birth:           editForm.date_of_birth,
        sex:                     editForm.sex,
        phone:                   editForm.phone,
        church,
        emergency_contact_name:  editForm.emergency_contact_name,
        emergency_contact_phone: editForm.emergency_contact_phone,
        needs_transport:         editForm.needs_transport,
        amount_owed:             newAmountOwed,
      })
      .eq('id', editingRegId)

    if (error) {
      setSaveError(error.message)
      setSaving(false)
    } else {
      await fetchAll()
      stopEditing()
      setSaving(false)
    }
  }

  /* ── Unregister ────────────────────────────────────────────────────── */
  async function handleCancel() {
    setCancelling(true)
    // Fix #2: handle error — keep the confirm dialog open so the user can retry
    const { error } = await supabase
      .from('registrations')
      .update({
        payment_status: 'Cancelled',
        cancelled_by:   'user',
        cancelled_at:   new Date().toISOString(),
      })
      .eq('id', confirmingCancelRegId)
    setCancelling(false)
    if (error) {
      setSaveError(error.message)
      return
    }
    setConfirmingCancelRegId(null)
    await fetchAll()
  }

  /* ── Payment upload ────────────────────────────────────────────────── */
  function openUpload(regId) {
    setUploadingRegId(regId)
    setUploadForm({ file: null, amount: '', payment_method: '' })
    setUploadError('')
    setUploadSuccess(false)
  }

  function closeUpload() {
    setUploadingRegId(null)
    setUploadForm({ file: null, amount: '', payment_method: '' })
    setUploadError('')
    setUploadSuccess(false)
  }

  async function handlePaymentUpload(reg) {
    // Guard against Sponsored registrations bypassing the hidden UI
    if (reg.payment_status === 'Sponsored') { setUploadError(t('myReg.errorSponsoredPayment')); return }

    // Page-specific: don't allow paying more than the remaining balance.
    const remaining = (reg.amount_owed ?? 0) - (reg.amount_paid ?? 0)
    if (uploadForm.amount && parseFloat(uploadForm.amount) > remaining + 0.005) {
      setUploadError(t('myReg.errorAmountExceedsBalance', { amount: formatCurrency(remaining) }))
      return
    }

    setUploadError('')
    setUploading(true)

    const ERR_KEYS = {
      NO_FILE:        'myReg.errorNoFile',
      FILE_TOO_LARGE: 'myReg.errorFileTooLarge',
      INVALID_AMOUNT: 'myReg.errorInvalidAmount',
      NO_METHOD:      'myReg.errorNoPaymentMethod',
    }

    try {
      await uploadReceipt({
        file:           uploadForm.file,
        registrationId: reg.id,
        campId:         reg.camp_id,
        userId:         user.id,
        amount:         uploadForm.amount,
        paymentMethod:  uploadForm.payment_method,
      })
      setUploadSuccess(true)
      setUploadForm({ file: null, amount: '', payment_method: '' })
      // Expand the payment history for this registration so user sees it
      setExpandedPayments(prev => new Set([...prev, reg.id]))
      await fetchAll()
    } catch (err) {
      setUploadError(ERR_KEYS[err?.code] ? t(ERR_KEYS[err.code]) : (err?.message || String(err)))
    } finally {
      setUploading(false)
    }
  }

  /* ── Render ──────────────────────────────────────────────────────────── */
  if (pageLoading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-64px)]">
        <div className="w-8 h-8 border-4 border-oasis-500 border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  // Fix #3: show error state instead of empty "No registrations" on fetch failure
  if (pageError) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-64px)]">
        <div className="bg-white rounded-2xl shadow-lg p-12 text-center max-w-md">
          <span className="text-5xl">⚠️</span>
          <h2 className="mt-4 text-xl font-bold text-gray-900">{t('myReg.errorTitle')}</h2>
          <p className="mt-2 text-sm text-red-600">{pageError}</p>
          <button
            onClick={() => { setPageError(''); setPageLoading(true); fetchAll() }}
            className="mt-6 inline-block rounded-xl bg-oasis-500 px-6 py-2.5 text-sm font-semibold text-white hover:bg-oasis-700 transition"
          >
            {t('myReg.tryAgain')}
          </button>
        </div>
      </div>
    )
  }

  if (regs.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-64px)]">
        <div className="bg-white rounded-2xl shadow-lg p-12 text-center max-w-md">
          <span className="text-5xl">📋</span>
          <h2 className="mt-4 text-xl font-bold text-gray-900">{t('myReg.notFoundTitle')}</h2>
          <p className="mt-2 text-sm text-gray-500">{t('myReg.notFoundBody')}</p>
          <Link
            to="/register-camp"
            className="mt-6 inline-block rounded-xl bg-oasis-500 px-6 py-2.5 text-sm font-semibold text-white hover:bg-oasis-700 transition"
          >
            {t('myReg.registerNow')}
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-2xl mx-auto space-y-10">

      {/* ── Page title ─────────────────────────────────────────── */}
      <h1 className="text-2xl font-bold text-gray-900">
        {regs.length > 1 ? t('myReg.titlePlural') : t('myReg.title')}
      </h1>

      {regs.map(reg => {
        const camp = reg.camps
        const tier = reg.pricing_tiers
        const isEditing = editingRegId === reg.id
        const isConfirmingCancel = confirmingCancelRegId === reg.id
        const isUploadOpen = uploadingRegId === reg.id

        const regReceipts = receiptsFor(reg.id)
        const isPaid = reg.payment_status === 'Paid'
        const isSponsored = reg.payment_status === 'Sponsored'
        const amountPaid = reg.amount_paid ?? 0
        const amountOwed = reg.amount_owed ?? 0
        const remaining = Math.max(0, amountOwed - amountPaid)
        const progressPct = amountOwed > 0 ? Math.min(100, (amountPaid / amountOwed) * 100) : 0
        const hasPendingReceipt = regReceipts.some(r => r.status === 'Pending Review')
        const paymentExpanded = expandedPayments.has(reg.id)

        return (
          <div key={reg.id} className="space-y-4">

            {/* ── Camp summary card ─────────────────────────── */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="bg-oasis-500 px-4 sm:px-6 py-3 flex items-center justify-between gap-2">
                <span className="text-xs font-semibold uppercase tracking-widest text-oasis-100 truncate">
                  {camp?.name ?? 'Camp'}
                </span>
                <div className="flex items-center gap-3">
                  <StatusBadge status={reg.payment_status} />
                  {!isEditing && !isUploadOpen && (
                    <button
                      onClick={() => startEditing(reg)}
                      className="rounded-md border border-oasis-300 bg-oasis-500 px-3 py-1 text-xs font-medium text-white hover:bg-oasis-400 transition"
                    >
                      {t('myReg.editBtn')}
                    </button>
                  )}
                </div>
              </div>
              <div className="p-6 grid grid-cols-2 sm:grid-cols-3 gap-5">
                {camp?.start_date && <DetailRow label={t('myReg.campStarts')} value={formatDate(camp.start_date, i18n.language)} />}
                {camp?.end_date   && <DetailRow label={t('myReg.campEnds')}   value={formatDate(camp.end_date, i18n.language)} />}
                {camp?.location   && <DetailRow label={t('myReg.location')}    value={camp.location} />}
                <DetailRow label={t('myReg.package')}      value={tier?.label ?? '—'} />
                <DetailRow label={t('myReg.arrivalDate')} value={tier?.arrival_date ? formatDate(tier.arrival_date, i18n.language) : '—'} />
                <DetailRow label={t('myReg.campFee')}     value={formatCurrency(tier?.price ?? 0)} />
                {reg.needs_transport && (camp?.transport_cost ?? 0) > 0 && (
                  <DetailRow label={t('myReg.transportFee')} value={formatCurrency(camp.transport_cost)} />
                )}
                <DetailRow label={t('myReg.totalFee')}  value={formatCurrency(amountOwed)} />
                <DetailRow label={t('myReg.transportNeeded')} value={reg.needs_transport ? t('myReg.yes') : t('myReg.no')} />
              </div>
            </div>

            {/* ── Edit form ─────────────────────────────────── */}
            {isEditing && editForm ? (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 space-y-6">
                <h2 className="text-base font-semibold text-gray-900 pb-3 border-b border-gray-100">
                  {t('myReg.editTitle', { campName: camp?.name })}
                </h2>

                {saveError && (
                  <div className="flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
                    <span className="mt-0.5 shrink-0">⚠️</span>
                    <span>{saveError}</span>
                  </div>
                )}

                {/* Personal */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="sm:col-span-2">
                    <label className={LABEL}>{t('myReg.fullName')}</label>
                    <input type="text" required maxLength={100} value={editForm.full_name}
                      onChange={e => setField('full_name', e.target.value)} className={INPUT} />
                  </div>
                  <div>
                    <label className={LABEL}>{t('myReg.dateOfBirth')}</label>
                    <input type="date" required value={editForm.date_of_birth}
                      max={new Date().toISOString().split('T')[0]}
                      onChange={e => setField('date_of_birth', e.target.value)} className={INPUT} />
                  </div>
                  <div>
                    <label className={LABEL}>{t('myReg.sex')}</label>
                    <select required value={editForm.sex}
                      onChange={e => setField('sex', e.target.value)} className={INPUT}>
                      <option value="">{t('myReg.sexSelect')}</option>
                      <option value="Male">{t('myReg.sexMale')}</option>
                      <option value="Female">{t('myReg.sexFemale')}</option>
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <label className={LABEL}>{t('myReg.phone')}</label>
                    <input type="tel" required maxLength={20} value={editForm.phone}
                      onChange={e => setField('phone', e.target.value)} className={INPUT} />
                  </div>
                  <div className="sm:col-span-2">
                    <label className={LABEL}>{t('myReg.church')}</label>
                    <select required value={editForm.church}
                      onChange={e => setField('church', e.target.value)} className={INPUT}>
                      <option value="">{t('myReg.churchSelect')}</option>
                      {churches.map(c => (
                        <option key={c.id} value={c.name}>{c.name}</option>
                      ))}
                      <option value={OTHER_CHURCH}>{t('myReg.churchOther')}</option>
                    </select>
                  </div>
                  {editForm.church === OTHER_CHURCH && (
                    <div className="sm:col-span-2">
                      <label className={LABEL}>{t('myReg.churchOtherLabel')}</label>
                      <input type="text" required maxLength={100}
                        placeholder={t('myReg.churchOtherPlaceholder')}
                        value={editForm.church_other}
                        onChange={e => setField('church_other', e.target.value)} className={INPUT} />
                    </div>
                  )}
                </div>

                {/* Emergency contact */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 pt-2 border-t border-gray-100">
                  <div>
                    <label className={LABEL}>{t('myReg.emergencyContactName')}</label>
                    <input type="text" required maxLength={100} value={editForm.emergency_contact_name}
                      onChange={e => setField('emergency_contact_name', e.target.value)} className={INPUT} />
                  </div>
                  <div>
                    <label className={LABEL}>{t('myReg.emergencyContactPhone')}</label>
                    <input type="tel" required maxLength={20} value={editForm.emergency_contact_phone}
                      onChange={e => setField('emergency_contact_phone', e.target.value)} className={INPUT} />
                  </div>
                </div>

                {/* Transport */}
                <div className="space-y-5 pt-2 border-t border-gray-100">
                  <div>
                    <p className={LABEL}>{t('myReg.transportNeededQ')}</p>
                    <div className="flex gap-3 mt-1">
                      {[false, true].map(val => (
                        <button key={String(val)} type="button"
                          onClick={() => setField('needs_transport', val)}
                          className={`flex-1 sm:flex-none px-5 py-3 min-h-[44px] rounded-lg text-sm font-medium border transition ${
                            editForm.needs_transport === val
                              ? 'bg-oasis-500 text-white border-oasis-500 shadow-sm'
                              : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                          }`}>
                          {val ? t('myReg.yes') : t('myReg.no')}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Live fee preview */}
                  {(() => {
                    const baseFee = reg.pricing_tiers?.price ?? 0
                    const transportCost = parseFloat(reg.camps?.transport_cost ?? 0)
                    const total = baseFee + (editForm.needs_transport ? transportCost : 0)
                    return (
                      <div className="rounded-xl bg-gray-50 border border-gray-100 px-4 py-3 space-y-1.5 text-sm">
                        <div className="flex justify-between text-gray-500">
                          <span>{t('myReg.campFeeLabel')}</span>
                          <span>{formatCurrency(baseFee)}</span>
                        </div>
                        {transportCost > 0 && (
                          <div className={`flex justify-between ${editForm.needs_transport ? 'text-gray-500' : 'text-gray-400'}`}>
                            <span>{t('myReg.transportLabel')}</span>
                            <span>{editForm.needs_transport ? formatCurrency(transportCost) : t('myReg.transportIfNeeded', { amount: formatCurrency(transportCost) })}</span>
                          </div>
                        )}
                        <div className="flex justify-between font-semibold text-gray-900 border-t border-gray-200 pt-1.5">
                          <span>{t('myReg.totalLabel')}</span>
                          <span>{formatCurrency(total)}</span>
                        </div>
                      </div>
                    )
                  })()}
                </div>

                {/* Edit actions */}
                <div className="flex flex-col sm:flex-row gap-3 pt-2 border-t border-gray-100">
                  <button onClick={handleSave} disabled={saving}
                    className="w-full sm:w-auto rounded-lg bg-oasis-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-oasis-700 disabled:opacity-60 disabled:cursor-not-allowed transition">
                    {saving ? t('myReg.saving') : t('myReg.saveChanges')}
                  </button>
                  <button onClick={stopEditing} disabled={saving}
                    className="w-full sm:w-auto rounded-lg border border-gray-300 bg-white px-5 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition">
                    {t('myReg.cancel')}
                  </button>
                </div>
              </div>
            ) : (
              /* ── Read-only personal details ─────────────────── */
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                <h2 className="text-base font-semibold text-gray-900 mb-5 pb-3 border-b border-gray-100">
                  {t('myReg.personalDetails')}
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-5">
                  <DetailRow label={t('myReg.fullName')}           value={reg.full_name} />
                  <DetailRow label={t('myReg.dateOfBirth')}       value={formatDate(reg.date_of_birth, i18n.language)} />
                  <DetailRow label={t('myReg.sex')}                 value={reg.sex} />
                  <DetailRow label={t('myReg.phone')}               value={reg.phone} />
                  <DetailRow label={t('myReg.church')}              value={reg.church} />
                  <DetailRow label={t('myReg.emergencyContactName')}   value={reg.emergency_contact_name} />
                  <DetailRow label={t('myReg.emergencyContactPhone')}     value={reg.emergency_contact_phone} />
                </div>
              </div>
            )}

            {/* ── Sponsored notice ──────────────────────────── */}
            {isSponsored && !isEditing && (
              <div className="bg-purple-50 rounded-2xl border border-purple-200 p-5 flex items-start gap-3">
                <span className="text-2xl">🎁</span>
                <div>
                  <p className="text-sm font-semibold text-purple-800">{t('myReg.sponsoredTitle')}</p>
                  <p className="text-xs text-purple-700 mt-0.5">
                    {t('myReg.sponsoredBody')}
                  </p>
                </div>
              </div>
            )}

            {/* ── Payment section (non-Paid, non-Sponsored, non-Editing) ──── */}
            {!isPaid && !isSponsored && !isEditing && (
              <div className="bg-white rounded-2xl shadow-sm border border-oasis-100 overflow-hidden">

                {/* Progress bar header */}
                <div className="px-6 pt-5 pb-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-semibold text-gray-900">{t('myReg.paymentProgress')}</p>
                    <p className="text-sm font-bold text-oasis-700">
                      {formatCurrency(amountPaid)} <span className="font-normal text-gray-400">/ {formatCurrency(amountOwed)}</span>
                    </p>
                  </div>
                  <div className="w-full h-2.5 rounded-full bg-gray-100 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-oasis-500 transition-all duration-500"
                      style={{ width: `${progressPct}%` }}
                    />
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <p className="text-xs text-gray-500">
                      {progressPct > 0 ? t('myReg.percentPaid', { pct: Math.round(progressPct) }) : t('myReg.noPaymentsYet')}
                    </p>
                    <p className="text-xs font-medium text-gray-700">
                      {t('myReg.remaining')} <span className="font-semibold text-rose-600">{formatCurrency(remaining)}</span>
                    </p>
                  </div>
                </div>

                {/* Payment history */}
                {regReceipts.length > 0 && (
                  <div className="border-t border-gray-100">
                    <button
                      onClick={() => togglePaymentExpand(reg.id)}
                      className="w-full flex items-center justify-between px-6 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 transition"
                    >
                      <span>{t('myReg.paymentHistory', { count: regReceipts.length })}</span>
                      <span className="text-gray-400">{paymentExpanded ? '▲' : '▼'}</span>
                    </button>

                    {paymentExpanded && (
                      <div className="divide-y divide-gray-100">
                        {regReceipts.map(receipt => (
                          <div key={receipt.id} className="px-6 py-4 flex items-start justify-between gap-4">
                            <div className="min-w-0">
                              <p className="text-sm font-semibold text-gray-900">{formatCurrency(receipt.amount)}</p>
                              <p className="text-xs text-gray-500 mt-0.5">
                                {paymentMethodLabel(receipt.payment_method, t) ?? '—'} &nbsp;·&nbsp; {formatDateShort(receipt.created_at, i18n.language)}
                              </p>
                              {receipt.admin_note && receipt.status === 'Rejected' && (
                                <p className="mt-1 text-xs text-red-600 bg-red-50 rounded px-2 py-1 border border-red-100">
                                  ✕ {receipt.admin_note}
                                </p>
                              )}
                            </div>
                            <div className="flex items-center gap-3 shrink-0">
                              {receipt.receipt_url && (
                                <a
                                  href={receipt.receipt_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-xs text-oasis-500 hover:text-oasis-800 underline"
                                >
                                  {t('myReg.viewReceipt')}
                                </a>
                              )}
                              <StatusBadge status={receipt.status} styles={RECEIPT_STATUS_STYLES} />
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Upload section */}
                <div className="border-t border-gray-100 px-6 py-5">
                  {hasPendingReceipt && !isUploadOpen ? (
                    <div className="flex items-start gap-3">
                      <span className="text-lg mt-0.5">⏳</span>
                      <div>
                        <p className="text-sm font-semibold text-amber-800">{t('myReg.pendingReviewTitle')}</p>
                        <p className="text-xs text-amber-700 mt-0.5">
                          {t('myReg.pendingReviewBody')}
                        </p>
                      </div>
                    </div>
                  ) : isUploadOpen ? (
                    /* Upload form */
                    <div className="space-y-4">
                      <h3 className="text-sm font-semibold text-gray-900 pb-3 border-b border-gray-100">
                        {t('myReg.submitPaymentTitle')}
                      </h3>

                      {uploadSuccess && (
                        <div className="flex items-start gap-2 rounded-lg bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-800">
                          <span className="shrink-0 mt-0.5">✅</span>
                          <span>{t('myReg.receiptSubmitted')}</span>
                        </div>
                      )}

                      {uploadError && (
                        <div className="flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
                          <span className="shrink-0 mt-0.5">⚠️</span>
                          <span>{uploadError}</span>
                        </div>
                      )}

                      <div>
                        <label className={LABEL}>{t('myReg.receiptFile')} <span className="font-normal text-gray-400">{t('myReg.receiptFileHint')}</span></label>
                        <input
                          type="file"
                          accept="image/*,application/pdf"
                          onChange={e => setUploadForm(p => ({ ...p, file: e.target.files?.[0] ?? null }))}
                          className="w-full text-sm text-gray-600 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-oasis-50 file:text-oasis-700 hover:file:bg-oasis-100 transition"
                        />
                        {uploadForm.file && (
                          <p className="mt-1 text-xs text-gray-500">{t('myReg.selectedFile', { filename: uploadForm.file.name })}</p>
                        )}
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className={LABEL}>{t('myReg.amountPaid')}</label>
                          <input
                            type="number" min="0.01" step="0.01"
                            max={remaining}
                            placeholder={remaining > 0 ? String(remaining) : '0'}
                            value={uploadForm.amount}
                            onChange={e => setUploadForm(p => ({ ...p, amount: e.target.value }))}
                            className={INPUT}
                          />
                          {remaining > 0 && (
                            <p className="mt-1 text-xs text-gray-400">{t('myReg.maxAmount', { amount: formatCurrency(remaining) })}</p>
                          )}
                        </div>
                        <div>
                          <label className={LABEL}>{t('myReg.paymentMethod')}</label>
                          <select
                            value={uploadForm.payment_method}
                            onChange={e => setUploadForm(p => ({ ...p, payment_method: e.target.value }))}
                            className={INPUT}
                          >
                            <option value="">{t('myReg.paymentMethodSelect')}</option>
                            {PAYMENT_METHOD_CODES.map(code => (
                              <option key={code} value={code}>{paymentMethodLabel(code, t)}</option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="flex flex-col sm:flex-row gap-3 pt-2 border-t border-gray-100">
                        <button
                          onClick={() => handlePaymentUpload(reg)}
                          disabled={uploading}
                          className="w-full sm:w-auto rounded-lg bg-oasis-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-oasis-700 disabled:opacity-60 disabled:cursor-not-allowed transition"
                        >
                          {uploading ? t('myReg.uploading') : t('myReg.submitReceipt')}
                        </button>
                        <button
                          onClick={closeUpload}
                          disabled={uploading}
                          className="w-full sm:w-auto rounded-lg border border-gray-300 bg-white px-5 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition"
                        >
                          {t('myReg.cancel')}
                        </button>
                      </div>
                    </div>
                  ) : (
                    /* Upload prompt */
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <div>
                        <p className="text-sm font-semibold text-gray-900">{t('myReg.submitPaymentPromptTitle')}</p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {remaining > 0
                            ? t('myReg.submitPaymentPromptBody', { remaining: formatCurrency(remaining) })
                            : t('myReg.submitPaymentPromptBodyNoRemaining')}
                        </p>
                      </div>
                      <button
                        onClick={() => openUpload(reg.id)}
                        className="w-full sm:w-auto rounded-lg border border-oasis-300 bg-oasis-50 px-4 py-2.5 text-sm font-medium text-oasis-700 hover:bg-oasis-100 transition"
                      >
                        {t('myReg.addPayment')}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ── Paid notice ────────────────────────────────── */}
            {isPaid && !isEditing && (
              <div className="bg-green-50 rounded-2xl border border-green-200 p-5 flex items-start gap-3">
                <span className="text-2xl">✅</span>
                <div>
                  <p className="text-sm font-semibold text-green-800">{t('myReg.paidTitle')}</p>
                  <p className="text-xs text-green-700 mt-0.5">
                    {t('myReg.paidBody', { amount: formatCurrency(amountOwed) })}
                    {regReceipts.length > 0 && (
                      <button
                        onClick={() => togglePaymentExpand(reg.id)}
                        className="ml-2 underline"
                      >
                        {paymentExpanded ? t('myReg.hidePaymentHistory') : t('myReg.viewPaymentHistory')}
                      </button>
                    )}
                  </p>
                  {paymentExpanded && regReceipts.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {regReceipts.map(receipt => (
                        <div key={receipt.id} className="flex flex-wrap items-center justify-between gap-2 text-xs bg-white rounded-lg border border-green-100 px-3 py-2">
                          <span>{formatCurrency(receipt.amount)} via {paymentMethodLabel(receipt.payment_method, t) ?? '—'} · {formatDateShort(receipt.created_at, i18n.language)}</span>
                          <StatusBadge status={receipt.status} styles={RECEIPT_STATUS_STYLES} />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ── Installment schedule (read-only) ──────────── */}
            {agreements[reg.id] && (
              <div className="rounded-xl border border-outline p-4 mt-4">
                <h3 className="font-semibold mb-3">{t('myReg.agreementTitle')}</h3>
                {(installments[reg.id] ?? []).map(inst => {
                  const status = deriveInstallmentStatus(inst, allocations[reg.id] ?? [])
                  const isNextDue = status === 'Unpaid' &&
                    !(installments[reg.id] ?? []).some(o => o.seq < inst.seq && deriveInstallmentStatus(o, allocations[reg.id] ?? []) === 'Unpaid')
                  return (
                    <div key={inst.id}
                      className={`flex justify-between items-center py-1.5 text-sm ${isNextDue ? 'font-semibold text-primary' : ''}`}>
                      <span>#{inst.seq} · {inst.due_date}</span>
                      <span>
                        {formatCurrency(inst.amount)}
                        <span className={status === 'Paid' ? 'ml-2 text-green-700' : 'ml-2 text-amber-700'}>
                          {status === 'Paid' ? t('myReg.installmentPaid') : t('myReg.installmentUnpaid')}
                        </span>
                      </span>
                    </div>
                  )
                })}
              </div>
            )}

            {/* ── Unregister section ────────────────────────── */}
            {!isEditing && !isUploadOpen && (
              <div className="bg-white rounded-2xl shadow-sm border border-red-100 p-6">
                {isConfirmingCancel ? (
                  <div>
                    <p className="text-sm font-semibold text-gray-900">
                      {t('myReg.unregisterConfirmTitle', { campName: camp?.name })}
                    </p>
                    <p className="mt-1 text-sm text-gray-500">
                      {t('myReg.unregisterConfirmBody')}
                    </p>
                    <div className="mt-4 flex flex-col sm:flex-row gap-3">
                      <button onClick={handleCancel} disabled={cancelling}
                        className="w-full sm:w-auto rounded-lg bg-red-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-60 transition">
                        {cancelling ? t('myReg.cancelling') : t('myReg.yesUnregister')}
                      </button>
                      <button onClick={() => setConfirmingCancelRegId(null)} disabled={cancelling}
                        className="w-full sm:w-auto rounded-lg border border-gray-300 bg-white px-5 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition">
                        {t('myReg.keepRegistration')}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{t('myReg.cancelTitle')}</p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {t('myReg.cancelBody', { campName: camp?.name ?? 'this camp' })}
                      </p>
                    </div>
                    <button
                      onClick={() => setConfirmingCancelRegId(reg.id)}
                      className="w-full sm:w-auto rounded-lg border border-red-300 bg-white px-4 py-2.5 text-sm font-medium text-red-600 hover:bg-red-50 transition"
                    >
                      {t('myReg.unregister')}
                    </button>
                  </div>
                )}
              </div>
            )}

          </div>
        )
      })}

    </div>
  )
}
